import { getProjectAddressString } from '../../components/project/project.util';
import { format, formatCurrency, formatPercentage, formatISODate } from '../../common/utils/common.util';
import { REPORT_COLORS } from '../../components/report/constants';
import { createCard } from './Card';
import { createCostAnalysisDivisionTable, renderTotalsRow } from './CostAnalysisDivisionTable';
import { CA_REPORT, CostComparison, CostPerUnit } from '../../components/report/report.type';
import { Division, LineItemLabelEnum } from '../../components/glossary/glossary.type';
import { parseDynamicDataString } from '../../components/report/report.util';
import { NoteDTO } from 'common/utils/util.type';
import { UNDEFINED_CHANGE_ORDER_DIVISION_CODE } from '../constants/general';

export interface CostAnalysisReportProps {
  report: CA_REPORT;
  costPerUnit: CostPerUnit;
  costComparison: CostComparison;
  sortedDivisions: Division[];
  sortedTables: any;
  hardCostSubTotalRow: any;
  projectedHardCostSubTotalRow: any;
  softCostSubTotalRow: any;
  totalProjectCostsRow: any;
  revisedBudget: number;
  notes: NoteDTO[];
}

export function createCostAnalysisReportPdf({
  report,
  costPerUnit,
  costComparison,
  sortedDivisions,
  sortedTables,
  hardCostSubTotalRow,
  projectedHardCostSubTotalRow,
  softCostSubTotalRow,
  totalProjectCostsRow,
  revisedBudget,
  notes,
}: CostAnalysisReportProps) {
  const sortedNotes = [...notes].sort((a: any, b: any) => new Date(b.createdOn).getTime() - new Date(a.createdOn).getTime());
  const dynamicData = parseDynamicDataString(report.dynamicData);
  const othersCardData = Object.keys(dynamicData)
    .map(key => ({
      key: key,
      value: format(dynamicData[key]?.toString() || '--', 'number'),
    }))
    .filter(data => data.value !== '--');

  const content = [
    createCard({
      header: report.generalInfo.reportName || 'Cost Analysis Report',
      headerFontSize: 20,
      headerAlignment: 'center',
      itemsPerRow: 3,
      layout: 'headerLineOnly',
      data: [
        { key: 'Project', value: report?.project?.projectTitle || '--' },
        { key: 'Location', value: getProjectAddressString(report.project.projectAddress) || '--' },
        { key: 'Project #', value: report?.project?.projectNumber || '--' },
        ...(report?.costDataFrom && report?.budgetDated
          ? [
              {
                key: '',
                value: `Cost Data from ${report?.costDataFrom || '--'} - SOV dated ${
                  formatISODate(report?.budgetDated || '', true) || '--'
                }`,
              },
            ]
          : []),
      ],
    }),
    {
      columns: [
        createCard({
          header: 'General Info',
          itemsPerRow: 1,
          layout: 'headerLineOnly',
          data: [
            ...(report.generalInfo.grossBuildingSF
              ? [{ key: 'Gross Building SF', value: format(report.generalInfo.grossBuildingSF.toString(), 'number') }]
              : []),
            ...(report.generalInfo.netRentableSF
              ? [{ key: 'Net Rentable SF', value: format(report.generalInfo.netRentableSF.toString(), 'number') }]
              : []),
            ...(report.generalInfo.buildingCount
              ? [{ key: '# Apt Buildings', value: format(report.generalInfo.buildingCount.toString(), 'number') }]
              : []),
            ...(report.generalInfo.storyCount
              ? [{ key: '# Stories', value: format(report.generalInfo.storyCount.toString(), 'number') }]
              : []),
            ...(report.generalInfo.unitCount ? [{ key: '# Units', value: format(report.generalInfo.unitCount.toString(), 'number') }] : []),
            ...(report.generalInfo.siteAcreage
              ? [{ key: 'Site Acreage', value: format(report.generalInfo.siteAcreage.toString(), 'number') }]
              : []),
          ],
        }),
        createCard({
          header: 'Budgeted Cost Per Unit',
          itemsPerRow: 1,
          backgroundColor: REPORT_COLORS.BLUE,
          data: [
            ...(costPerUnit.hardCostPerBuildingGSF
              ? [{ key: 'Hard Cost / Building GSF', value: formatCurrency(costPerUnit.hardCostPerBuildingGSF.toString()) }]
              : []),
            ...(costPerUnit.projectCostPerBuildingNRSF
              ? [{ key: 'Project Cost / Building NRSF', value: formatCurrency(costPerUnit.projectCostPerBuildingNRSF.toString()) }]
              : []),
            ...(costPerUnit.projectCostPerUnit
              ? [{ key: 'Project Cost / Unit', value: formatCurrency(costPerUnit.projectCostPerUnit.toString()) }]
              : []),
          ],
        }),
        createCard({
          header: 'Hard Cost Comparison',
          itemsPerRow: 1,
          backgroundColor: REPORT_COLORS.BLUE,
          data: [
            { key: 'Contractor Hard Cost Total', value: formatCurrency(costComparison.contractorHardCostTotal.toString()) },
            { key: 'CDCC Hard Cost Total', value: formatCurrency(costComparison.cdccHardCostTotal.toString()) },
            { key: 'CDCC Cost / GSF', value: formatCurrency(costComparison.cdccCostPerGSF.toString()) },
            { key: '% Deviation', value: formatPercentage((costComparison.deviationPercentage * 100).toString()) },
          ],
        }),
        ...(othersCardData.length > 0
          ? [
              createCard({
                header: 'Others',
                itemsPerRow: 1,
                layout: 'headerLineOnly',
                data: othersCardData,
              }),
            ]
          : []),
      ],
    },
  ];

  sortedDivisions.forEach((division, index) => {
    if (division.code === UNDEFINED_CHANGE_ORDER_DIVISION_CODE) return;

    content.push(
      createCostAnalysisDivisionTable({
        division: division,
        lineItems: sortedTables[index],
        grossBuildingSF: report.generalInfo.grossBuildingSF ?? 0,
        hardCostSubTotal: projectedHardCostSubTotalRow[0].totalBudget,
      }),
    );

    // Commented out till we implement both internal and external notes
    // if (division.note?.content) {
    //   content.push(
    //     createCard({
    //       header: `NOTES (${division.name}) `,
    //       itemsPerRow: 1,
    //       layout: grayHeaderLineLayout,
    //       data: [{ key: '', value: division.note?.content }],
    //       textColor: '#808080',
    //     }),
    //   );
    // }
    if (index == 15) {
      content.push(renderTotalsRow(hardCostSubTotalRow[0], REPORT_COLORS.HARD_COST_SUBTOTAL_COLOR));
    }
    if (index == 17) {
      content.push(renderTotalsRow(projectedHardCostSubTotalRow[0], REPORT_COLORS.PROJECTED_HARD_COST_SUBTOTAL_COLOR));
    }
    if (index == 19) {
      content.push(renderTotalsRow(softCostSubTotalRow[0], REPORT_COLORS.SOFT_COST_SUBTOTAL_COLOR));
    }
  });
  content.push(renderTotalsRow(totalProjectCostsRow[0], REPORT_COLORS.TOTAL_COST_COLOR));
  content.push(
    createCard({
      header: 'Grand Totals',
      itemsPerRow: 3,
      layout: 'headerLineOnly',
      data: [{ key: 'Revised Budget', value: formatCurrency(revisedBudget.toString()) }],
    }),
  );
  if (sortedNotes.length > 0) {
    content.push(
      createCard({
        header: 'Notes',
        itemsPerRow: 1,
        layout: 'headerLineOnly',
        data: [...sortedNotes.map((note, index) => ({ key: (index + 1).toString(), value: note.content }))],
      }),
    );
  }
  content.push(
    createCostAnalysisDivisionTable({
      division: null,
      lineItems: report.lineItems.filter(li => li.label === LineItemLabelEnum.Allowances),
      grossBuildingSF: report.generalInfo.grossBuildingSF ?? 0,
      hardCostSubTotal: projectedHardCostSubTotalRow[0].totalBudget,
      totalProjectCost: totalProjectCostsRow[0].totalBudget,
      name: 'ALLOWANCES',
    }),
  );
  return content;
}
